import * as moment from "moment";
/** Maximum number of records submitted in a single import request. */
export const MAX_IMPORT_RECORDS = 2000;

export type PersonFieldKey =
  | "FirstName"
  | "MiddleName"
  | "Surname"
  | "Alias"
  | "Gender"
  | "DateOfBirth"
  | "PhoneNumber"
  | "EmailAddress";

export type FieldMapping = Partial<Record<PersonFieldKey, string>>;

export type ImportStatus = "idle" | "running" | "done";

export type ImportResults = {
  total: number;
  successful: number;
  failed: number;
  downloadUrl: string | null;
};

export const PERSON_FIELDS: Array<{
  key: PersonFieldKey;
  label: string;
  required?: boolean;
}> = [
  { key: "FirstName", label: "First Name", required: true },
  { key: "Surname", label: "Surname", required: true },
  { key: "MiddleName", label: "Middle Name" },
  { key: "Alias", label: "Alias" },
  { key: "Gender", label: "Gender" },
  { key: "DateOfBirth", label: "Date of Birth" },
  { key: "PhoneNumber", label: "Phone Number" },
  { key: "EmailAddress", label: "Email Address" },
];

function normalizeStr(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function similarityScore(a: string, b: string): number {
  if (a === b) return 2;
  if (a.includes(b) || b.includes(a)) {
    return Math.min(a.length, b.length) / Math.max(a.length, b.length);
  }
  return 0;
}

export function autoMapFields(csvHeaders: string[]): FieldMapping {
  const mapping: FieldMapping = {};
  const claimed = new Set<string>();

  for (const field of PERSON_FIELDS) {
    const normField = normalizeStr(field.key);
    let bestHeader = "";
    let bestScore = 0;

    for (const header of csvHeaders) {
      if (claimed.has(header)) continue;
      const score = similarityScore(normField, normalizeStr(header));
      if (score > bestScore) {
        bestScore = score;
        bestHeader = header;
      }
    }

    if (bestScore > 0) {
      mapping[field.key] = bestHeader;
      claimed.add(bestHeader);
    }
  }

  return mapping;
}

/** Parses a DD/MM/YYYY date string into YYYY-MM-DD (ISO), or returns null. */
function parseDateOfBirth(raw: string | null): string | null {
  if (!raw) return null;
  const match = raw.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/);
  if (!match) return raw;
  const [, d, m, y] = match;
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
}

export function mapRowToRecord(
  row: Record<string, string>,
  mappings: FieldMapping,
) {
  const get = (key: PersonFieldKey): string | null => {
    const col = mappings[key];
    if (!col) return null;
    return row[col]?.trim() || null;
  };

  return {
    firstName: get("FirstName") ?? "",
    middleName: get("MiddleName"),
    surname: get("Surname") ?? "",
    alias: get("Alias"),
    gender: get("Gender"),
    dateOfBirth:
      (parseDateOfBirth(get("DateOfBirth")) &&
        moment(get("DateOfBirth"), "DD/MM/YYYY")
          .toDate()
          .toLocaleDateString("en-CA")) ||
      null,
    phoneNumber: get("PhoneNumber"),
    emailAddress: get("EmailAddress"),
  };
}
